<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

/**
 * SchemaFieldItem - A component for displaying schema field templates
 * Similar to ToolboxItem but optimized for field templates with click actions
 */
const props = defineProps({
  fieldName: {
    type: String,
    required: true,
    // e.g., 'location', 'space', 'title', etc.
  },
  fieldType: {
    type: String,
    required: true,
    // e.g., 'geo', 'keyword', 'text', etc.
  },
  description: {
    type: String,
    required: true,
    // Description shown on hover
  },
  icon: {
    type: String,
    required: false,
    default: 'i-lucide-plus',
  },
});

// Emit events
const emit = defineEmits(['click']);

// Computed property for field type styling
const fieldTypeStyles = computed(() => {
  const styles = {
    geo: {
      bg: 'bg-blue-100 dark:bg-blue-900',
      text: 'text-blue-800 dark:text-blue-200',
      border: 'border-blue-200 dark:border-blue-800',
    },
    keyword: {
      bg: 'bg-green-100 dark:bg-green-900',
      text: 'text-green-800 dark:text-green-200',
      border: 'border-green-200 dark:border-green-800',
    },
    text: {
      bg: 'bg-purple-100 dark:bg-purple-900',
      text: 'text-purple-800 dark:text-purple-200',
      border: 'border-purple-200 dark:border-purple-800',
    },
    integer: {
      bg: 'bg-orange-100 dark:bg-orange-900',
      text: 'text-orange-800 dark:text-orange-200',
      border: 'border-orange-200 dark:border-orange-800',
    },
    number: {
      bg: 'bg-amber-100 dark:bg-amber-900',
      text: 'text-amber-800 dark:text-amber-200',
      border: 'border-amber-200 dark:border-amber-800',
    },
    boolean: {
      bg: 'bg-indigo-100 dark:bg-indigo-900',
      text: 'text-indigo-800 dark:text-indigo-200',
      border: 'border-indigo-200 dark:border-indigo-800',
    },
    date: {
      bg: 'bg-rose-100 dark:bg-rose-900',
      text: 'text-rose-800 dark:text-rose-200',
      border: 'border-rose-200 dark:border-rose-800',
    },
    // Default style for unknown types
    default: {
      bg: 'bg-gray-100 dark:bg-gray-900',
      text: 'text-gray-800 dark:text-gray-200',
      border: 'border-gray-200 dark:border-gray-800',
    },
  };

  return styles[props.fieldType] || styles.default;
});

// Computed property to check if item has a Lucide icon
const hasIcon = computed(() => {
  return props.icon && props.icon.startsWith('i-lucide-');
});

// Methods
function handleClick() {
  emit('click');
}
</script>

<template>
  <button
    v-tooltip.top="description"
    type="button"
    class="flex-shrink-0 p-1 rounded-lg border border-n-slate-3 dark:border-n-slate-8 hover:bg-n-slate-2 dark:hover:bg-n-slate-9 transition-all duration-200 hover:-translate-y-px group"
    @click="handleClick"
  >
    <span
      class="inline-flex items-center gap-1 rounded-xl text-xs py-1 px-2 font-medium border"
      :class="[
        fieldTypeStyles.bg,
        fieldTypeStyles.text,
        fieldTypeStyles.border,
      ]"
    >
      <Icon v-if="hasIcon" :icon="icon" class="h-3 w-3 flex-shrink-0" />
      {{ fieldName }}
    </span>
  </button>
</template>
